import { API_CONFIG } from '../config/api';
import { permissionService } from './permissionService';

export interface CheckUserResponse {
  hasPassword: boolean;
  userId: string;
  userName: string;
  message: string;
}

export interface LoginResponse {
  message: string;
  userId: string;
  userName: string;
  twoFactorEnabled: boolean;
  requiresTwoFactor?: boolean;
}

export interface TwoFactorSetupResponse {
  sharedKey?: string;
  qrCodeUri?: string;
  isEnabled: boolean;
  message: string;
}

export interface EnableTwoFactorResponse {
  message: string;
  recoveryCodes: string[];
}

export interface CheckUserRequest {
  email: string;
}

export interface SetInitialPasswordRequest {
  email: string;
  password: string;
  confirmPassword: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe: boolean;
}

export interface VerifyTwoFactorRequest {
  code: string;
  rememberMe: boolean;
  rememberMachine: boolean;
}

export interface EnableTwoFactorRequest {
  code: string;
}

export interface SendResetCodeRequest {
  email: string;
}

export interface VerifyResetCodeRequest {
  email: string;
  code: string;
}

export interface ResetPasswordRequest {
  email: string;
  code: string;
  newPassword: string;
}

export interface SendResetCodeResponse {
  message: string;
  developmentCode?: string;
}

export interface VerifyResetCodeResponse {
  message: string;
  isValid: boolean;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface MyProfileResponse {
  userId: string;
  userName: string;
  email: string;
  phoneNumber: string;
}

export interface UpdateMyProfileRequest {
  userName: string;
  email: string;
  phoneNumber: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

const API_BASE_URL = API_CONFIG.baseURL;

class AuthService {
  
  async checkUser(request: CheckUserRequest): Promise<CheckUserResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/check-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Hiba történt a felhasználó ellenőrzése során');
    }

    return response.json();
  }

  async setInitialPassword(request: SetInitialPasswordRequest): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/set-initial-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Hiba történt a jelszó beállítása során');
    }

    return response.json();
  }

  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Hiba történt a bejelentkezés során');
    }

    const result = await response.json();
    
    // Store user info in localStorage
    if (result.userId) {
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userId', result.userId);
      localStorage.setItem('userName', result.userName);
      localStorage.setItem('userEmail', request.email);
      
      // Fetch and store user roles
      await this.fetchAndStoreUserRoles(result.userId);
      
      // Load user permissions
      await permissionService.loadPermissions();
    }

    return result;
  }

  async verifyTwoFactor(request: VerifyTwoFactorRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/verify-2fa`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Hiba történt a kétfaktoros hitelesítés során');
    }

    const result = await response.json();
    
    // Store user info in localStorage
    if (result.userId) {
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userId', result.userId);
      localStorage.setItem('userName', result.userName);
      
      // Fetch and store user roles
      await this.fetchAndStoreUserRoles(result.userId);
      
      // Load user permissions
      await permissionService.loadPermissions();
    }

    return result;
  }

  async getTwoFactorSetup(): Promise<TwoFactorSetupResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/2fa-setup`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Hiba történt a kétfaktoros hitelesítés beállítása során');
    }

    return response.json();
  }

  async enableTwoFactor(request: EnableTwoFactorRequest): Promise<EnableTwoFactorResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/enable-2fa`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Hiba történt a kétfaktoros hitelesítés bekapcsolása során');
    }

    return response.json();
  }

  async logout(): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    // Clear localStorage regardless of response
    this.clearLocalStorage();

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Hiba történt a kijelentkezés során');
    }

    return response.json();
  }

  // Check if user is logged in
  isLoggedIn(): boolean {
    return localStorage.getItem('isLoggedIn') === 'true';
  }

  // Validate current session with backend
  async validateSession(): Promise<boolean> {
    if (!this.isLoggedIn()) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/validate-session`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // Session is invalid, clear localStorage
        this.clearLocalStorage();
        return false;
      }

      // Mindig frissítjük a jogosultságokat, hogy szerepkör-módosítások is érvényesüljenek
      await permissionService.loadPermissions();

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      // On error, clear localStorage to be safe
      this.clearLocalStorage();
      return false;
    }
  }

  // Helper to clear all auth data from localStorage
  private clearLocalStorage(): void {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRoles');
    // Clear permissions
    permissionService.clearPermissions();
  }

  // Fetch and store user roles
  async fetchAndStoreUserRoles(userId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/userroles/user/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const userRoles = await response.json();
        const roleNames = userRoles.map((ur: any) => ur.roleName);
        localStorage.setItem('userRoles', JSON.stringify(roleNames));
      }
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
    }
  }

  // Get current user info
  getCurrentUser(): { userId: string; userName: string; email: string; roles: string[] } | null {
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName');
    const email = localStorage.getItem('userEmail');
    const rolesJson = localStorage.getItem('userRoles');
    
    if (userId && userName) {
      return { 
        userId, 
        userName,
        email: email || '',
        roles: rolesJson ? JSON.parse(rolesJson) : []
      };
    }
    
    return null;
  }

  // Get current user roles
  getCurrentUserRoles(): string[] {
    const roles = localStorage.getItem('userRoles');
    return roles ? JSON.parse(roles) : [];
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    const roles = this.getCurrentUserRoles();
    return roles.includes(role);
  }

  // Check if user can modify (Admin, Vezető tervező, Szerkesztő)
  canModify(): boolean {
    return this.hasRole('Adminisztrátor') || this.hasRole('Vezető tervező') || this.hasRole('Szerkesztő');
  }

  // Check if user can delete (Admin, Vezető tervező)
  canDelete(): boolean {
    return this.hasRole('Adminisztrátor') || this.hasRole('Vezető tervező');
  }

  // Check if user can view time tracking (not Külsős)
  canViewTimeTracking(): boolean {
    return !this.hasRole('Külsős');
  }

  // Send password reset code
  async sendResetCode(email: string): Promise<SendResetCodeResponse> {
    console.log('🔵 sendResetCode called with email:', email);
    console.log('🔵 API URL:', `${API_BASE_URL}/auth/send-reset-code`);
    
    const response = await fetch(`${API_BASE_URL}/auth/send-reset-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    console.log('🔵 Response status:', response.status, response.statusText);

    if (!response.ok) {
      const error = await response.json();
      console.error('🔴 Error response:', error);
      throw new Error(error.message || 'Hiba történt a kód küldése során');
    }

    const data = await response.json();
    console.log('🟢 Success response:', data);
    return data;
  }

  // Verify password reset code
  async verifyResetCode(email: string, code: string): Promise<VerifyResetCodeResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/verify-reset-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, code }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Hiba történt a kód ellenőrzése során');
    }

    return response.json();
  }

  // Reset password
  async resetPassword(email: string, code: string, newPassword: string): Promise<ResetPasswordResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, code, newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Hiba történt a jelszó beállítása során');
    }

    return response.json();
  }

  async getMyProfile(): Promise<MyProfileResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Nem sikerült lekérni a profil adatokat');
    }

    return response.json();
  }

  async updateMyProfile(request: UpdateMyProfileRequest): Promise<{ message: string; userName: string; email: string; phoneNumber: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Nem sikerült menteni a profil adatokat');
    }

    const result = await response.json();

    if (result.userName) {
      localStorage.setItem('userName', result.userName);
    }
    if (result.email) {
      localStorage.setItem('userEmail', result.email);
    }

    return result;
  }

  async changePassword(request: ChangePasswordRequest): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Nem sikerült megváltoztatni a jelszót');
    }

    return response.json();
  }
}

export const authService = new AuthService();
